package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry

import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.saves.SaveOutcome
import com.evoapps.evoshift.saves.isSavedOrKept
import com.evoapps.evoshift.saves.runOrKeepOnDevice
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_session_expired
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.model.WeeklyAvailabilityTemplate
import com.evoapps.evoshift.domain.availability.AvailabilitySlotMapping
import com.evoapps.evoshift.domain.availability.AvailabilityBulk
import evoshift.composeapp.generated.resources.availability_all_set_available
import evoshift.composeapp.generated.resources.availability_nothing_to_copy
import evoshift.composeapp.generated.resources.availability_copied_previous
import com.evoapps.evoshift.domain.model.AvailabilityDay
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.HoursRange
import com.evoapps.evoshift.domain.usecase.availability.GetMyAvailabilityUseCase
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.availability.ApplyWeeklyAvailabilityTemplateUseCase
import com.evoapps.evoshift.domain.usecase.availability.GetWeeklyAvailabilityTemplatesUseCase
import com.evoapps.evoshift.domain.usecase.availability.SaveWeeklyAvailabilityTemplateUseCase
import com.evoapps.evoshift.domain.usecase.roster.GetRosterUseCase
import com.evoapps.evoshift.domain.usecase.user.SubmitAvailabilityUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import com.evoapps.evoshift.domain.capacity.monthRange
import com.evoapps.evoshift.domain.capacity.within
import com.evoapps.evoshift.domain.schedule.ScheduleRange
import com.evoapps.evoshift.domain.usecase.GetAvailabilityRequestUseCase
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.minus
import kotlinx.datetime.plus

data class MonthWeek(
    val weekNumber: Int,
    val startDate: LocalDate,
    val endDate: LocalDate
)

data class AvailabilityFormUiState(
    val rosterId: String = "",
    val workplaceId: String = "",
    val isLoading: Boolean = true,
    /** Sending the form: the send button shows it, the form stays on screen. */
    val isSaving: Boolean = false,
    val monthYear: String = "", // "March 2026"
    val monthIso: String = "", // "2026-03"
    val year: Int = 0,
    val month: Int = 0,
    val days: Map<Int, DayPreference> = emptyMap(),
    val availableSlots: List<AvailabilitySlot> = emptyList(),
    val selectedDay: Int? = null,
    val error: UiText? = null,
    val success: Boolean = false,
    val isFilled: Boolean = false,
    val isReadOnly: Boolean = false,
    val availableTemplates: List<WeeklyAvailabilityTemplate> = emptyList(),
    val monthWeeks: List<MonthWeek> = emptyList(),
    val showApplyDialog: Boolean = false,
    val firstDayOfWeek: Int = 1, // 1=Mon, 7=Sun
    /** What the person may do at the workplace: filling it in needs [Gate.SUBMIT_AVAILABILITY] (not once the plan ended). */
    val capabilities: Set<Capability> = emptySet(),
    /** The days of the month the manager asked about (SH-48: e.g. 1–15); null: the whole month or no request. */
    val requestedDays: IntRange? = null,
    /** When the answers are due, with [requestedDays]. */
    val requestDue: LocalDate? = null,
    /**
     * SH-135: the month as given once for all the person's workplaces (hours per day); [days] is
     * how this workplace sees it (its slots), the comment being the one edited here.
     */
    val sharedDays: Map<Int, AvailabilityDay> = emptyMap(),
    /** SH-135: the days whose comment is for this workplace only. */
    val commentOnlyHere: Set<Int> = emptySet(),
    /** SH-135: the workplaces (names) the month goes to once sent from here. */
    val goesTo: List<String> = emptyList(),
    /** SH-135: the person has more than one workplace (a comment can be for this one only). */
    val hasOtherWorkplaces: Boolean = false,
    /** SH-135: given already at another workplace, not yet sent to this one (it was not shared with it). */
    val givenElsewhere: Boolean = false,
    /** SH-165: the days picked by a long press, to set their status at once (empty: one day at a time). */
    val multiSelected: Set<Int> = emptySet(),
    /** SH-165: what a bulk action did ("Copied from last month…"), shown once. */
    val info: UiText? = null
) {
    /** SH-165: the days a bulk action changes: those the manager asked about. */
    fun requestedDays(): List<Int> {
        val parts = monthIso.split("-")
        if (parts.size != 2) return emptyList()
        val last = LocalDate(parts[0].toInt(), parts[1].toInt(), 1).plus(DatePeriod(months = 1)).minus(DatePeriod(days = 1)).day
        return (1..last).filter(::isRequested)
    }

    /** Whether [day] is one the manager asked about (every day without a partial request). */
    fun isRequested(day: Int): Boolean = requestedDays?.let { day in it } ?: true

    /** The form may be changed at all; else it stays read-only (the workplace's plan ended, SH-65). */
    val canSubmit: Boolean get() = capabilities.shows(Gate.SUBMIT_AVAILABILITY)
}

sealed interface AvailabilityFormEvent {
    data class SelectDay(val day: Int) : AvailabilityFormEvent
    data class UpdateDayPreference(val day: Int, val preference: DayPreference) :
        AvailabilityFormEvent

    /** SH-135: the hours the person can work that day (null: the whole day). */
    data class SetDayHours(val day: Int, val hours: HoursRange?) : AvailabilityFormEvent
    /** SH-135: the day's comment is for this workplace only, or for all of them. */
    data class SetCommentOnlyHere(val day: Int, val onlyHere: Boolean) : AvailabilityFormEvent

    data object EnableEditMode : AvailabilityFormEvent
    data object Submit : AvailabilityFormEvent
    data object ClearMessage : AvailabilityFormEvent

    data object OpenApplyDialog : AvailabilityFormEvent
    data object CloseApplyDialog : AvailabilityFormEvent
    data class ApplyTemplateToWeeks(val templateId: String, val weekNumbers: Set<Int>) :
        AvailabilityFormEvent

    /** SH-165: "As last month": the previous month copied by weekday (AvailabilityBulk). */
    data object CopyPreviousMonth : AvailabilityFormEvent
    /** SH-165: "Every day: available". */
    data object SetAllAvailable : AvailabilityFormEvent
    /** SH-165: a long press picks [day] for several at once (again: leaves it out); a tap then too. */
    data class ToggleMultiSelect(val day: Int) : AvailabilityFormEvent
    /** SH-165: the picked days all get [status]; the picking ends. */
    data class SetStatusForSelected(val status: AvailabilityStatus) : AvailabilityFormEvent
    data object ClearMultiSelect : AvailabilityFormEvent
}

class AvailabilityFormViewModel(
    private val rosterId: String,
    private val getRosterUseCase: GetRosterUseCase,
    private val getMyAvailabilityUseCase: GetMyAvailabilityUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getWorkplaceUseCase: GetWorkplaceUseCase,
    private val submitAvailabilityUseCase: SubmitAvailabilityUseCase,
    private val getWeeklyAvailabilityTemplatesUseCase: GetWeeklyAvailabilityTemplatesUseCase,
    private val saveWeeklyAvailabilityTemplateUseCase: SaveWeeklyAvailabilityTemplateUseCase,
    private val applyWeeklyAvailabilityTemplateUseCase: ApplyWeeklyAvailabilityTemplateUseCase,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val getAvailabilityRequestUseCase: GetAvailabilityRequestUseCase? = null,
    /** SH-148: the app's saves: a write started here finishes even if the screen is left. */
    private val saves: com.evoapps.evoshift.saves.PendingSaves = com.evoapps.evoshift.saves.PendingSaves(),
    /** SH-186: offline, a send unanswered for a few seconds counts as kept on this device. */
    private val connectivity: com.evoapps.evoshift.connectivity.ConnectivityWatcher = com.evoapps.evoshift.connectivity.ManualConnectivity()
) : ViewModel() {

    private val _uiState = MutableStateFlow(AvailabilityFormUiState(rosterId = rosterId))
    val uiState = _uiState.asStateFlow()

    init {
        loadData()
    }

    private fun loadData() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = it.monthIso.isBlank()) }

            // 1. Get Roster info
            val rosterResult = getRosterUseCase(rosterId)
            if (rosterResult.isFailure) {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        error = rosterResult.exceptionOrNull()?.toUiText()
                    )
                }
                return@launch
            }
            val roster = rosterResult.getOrThrow()
            val monthIso = roster.getMonthYear()
            val workplaceId = roster.workplaceId

            // 2. Get User
            val user = getCurrentUserUseCase().first()
            if (user == null) {
                _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_session_expired)) }
                return@launch
            }

            val capabilities = user.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId))

            // 3. Get Workplace Slots & Settings
            val workplaceResult = getWorkplaceUseCase(workplaceId)
            val workplace = workplaceResult.getOrNull()
            val slots = workplace?.settings?.definedAvailabilitySlots ?: emptyList()
            val firstDayOfWeek = workplace?.settings?.firstDayOfWeek ?: 1

            // 4. The month as given once for all workplaces (SH-135), seen with this workplace's slots
            val mine = getMyAvailabilityUseCase(user.uid, workplaceId, monthIso).getOrNull()
            val sharedDays = mine?.shared?.days.orEmpty()
            val onlyHere = sharedDays.filterValues { it.comment.isNullOrBlank() && !it.workplaceComments[workplaceId].isNullOrBlank() }.keys
            val isFilled = mine?.sentHere ?: false

            // 5. Load templates
            val templates = getWeeklyAvailabilityTemplatesUseCase(
                user.uid,
                workplaceId
            ).getOrDefault(emptyList())

            // 6. Calculate weeks in month
            val weeks = calculateWeeks(roster.year, roster.month, firstDayOfWeek)

            // 7. The days asked about, when the request covers part of the month (SH-48)
            val request = roster.availabilityRequestId?.let { getAvailabilityRequestUseCase?.invoke(it)?.getOrNull() }
            val requested = request?.period?.let { period ->
                val start = runCatching { LocalDate.parse(period.startDate.take(10)) }.getOrNull()
                val end = runCatching { LocalDate.parse(period.endDate.take(10)) }.getOrNull()
                val month = monthRange(roster.year, roster.month)
                if (start == null || end == null) null
                else ScheduleRange(minOf(start, end), maxOf(start, end)).within(month)?.takeIf { it != month }
            }

            _uiState.update {
                it.copy(
                    isLoading = false,
                    workplaceId = workplaceId,
                    monthIso = monthIso,
                    year = roster.year,
                    month = roster.month,
                    monthYear = "${roster.month}/${roster.year}",
                    days = display(sharedDays, onlyHere, slots, workplaceId),
                    sharedDays = sharedDays,
                    commentOnlyHere = onlyHere,
                    goesTo = mine?.goesTo?.map { it.name }.orEmpty(),
                    hasOtherWorkplaces = (mine?.goesTo?.size ?: 0) > 1 || (mine?.employers ?: 0) > 1,
                    givenElsewhere = mine != null && mine.shared.isFilled && !mine.sentHere,
                    availableSlots = slots,
                    selectedDay = requested?.start?.day ?: 1,
                    isFilled = isFilled,
                    isReadOnly = isFilled || !capabilities.shows(Gate.SUBMIT_AVAILABILITY),
                    availableTemplates = templates,
                    monthWeeks = weeks,
                    firstDayOfWeek = firstDayOfWeek,
                    capabilities = capabilities,
                    requestedDays = requested?.let { it.start.day..it.endInclusive.day },
                    requestDue = requested?.let { request?.dueDate?.take(10)?.let { due -> runCatching { LocalDate.parse(due) }.getOrNull() } }
                )
            }
        }
    }

    private fun calculateWeeks(year: Int, month: Int, firstDayOfWeek: Int): List<MonthWeek> {
        val weeks = mutableListOf<MonthWeek>()
        val firstDay = LocalDate(year, month, 1)
        val lastDay = if (month == 12) {
            LocalDate(year + 1, 1, 1).minus(DatePeriod(days = 1))
        } else {
            LocalDate(year, month + 1, 1).minus(DatePeriod(days = 1))
        }

        // targetEndDayOfWeek is the day BEFORE firstDayOfWeek
        val targetEndDayOfWeek = if (firstDayOfWeek == 1) 7 else firstDayOfWeek - 1

        var currentStart = firstDay
        var weekNum = 1

        while (currentStart <= lastDay) {
            var currentEnd = currentStart
            while (currentEnd.dayOfWeek.isoDayNumber != targetEndDayOfWeek && currentEnd < lastDay) {
                currentEnd = currentEnd.plus(DatePeriod(days = 1))
            }

            weeks.add(MonthWeek(weekNum++, currentStart, currentEnd))
            currentStart = currentEnd.plus(DatePeriod(days = 1))
        }

        return weeks
    }

    fun onEvent(event: AvailabilityFormEvent) {
        // Read-only once the workplace's plan ended (SH-65): looking around and messages only.
        val changes = event is AvailabilityFormEvent.UpdateDayPreference || event == AvailabilityFormEvent.EnableEditMode ||
            event == AvailabilityFormEvent.Submit || event is AvailabilityFormEvent.ApplyTemplateToWeeks ||
            event == AvailabilityFormEvent.CopyPreviousMonth || event == AvailabilityFormEvent.SetAllAvailable ||
            event is AvailabilityFormEvent.SetStatusForSelected || event is AvailabilityFormEvent.ToggleMultiSelect
        if (changes && !_uiState.value.canSubmit) return
        // SH-165: bulk changes only while the form is open for changes (not after sending).
        val bulk = event == AvailabilityFormEvent.CopyPreviousMonth || event == AvailabilityFormEvent.SetAllAvailable ||
            event is AvailabilityFormEvent.SetStatusForSelected || event is AvailabilityFormEvent.ToggleMultiSelect
        if (bulk && _uiState.value.isReadOnly) return
        when (event) {
            is AvailabilityFormEvent.SelectDay -> {
                _uiState.update { it.copy(selectedDay = event.day) }
            }

            is AvailabilityFormEvent.UpdateDayPreference -> {
                changeDay(event.day) { state, old -> edited(state, event.day, old, event.preference) }
            }

            is AvailabilityFormEvent.SetDayHours -> {
                changeDay(event.day) { _, old -> old.copy(ranges = listOfNotNull(event.hours?.takeIf { it.minutes != null })) }
            }

            is AvailabilityFormEvent.SetCommentOnlyHere -> {
                val state = _uiState.value
                val day = state.sharedDays[event.day] ?: AvailabilityDay(AvailabilityStatus.AVAILABLE)
                val wp = state.workplaceId
                val text = (if (event.day in state.commentOnlyHere) day.workplaceComments[wp] else day.comment).orEmpty()
                val moved = if (event.onlyHere) {
                    day.copy(comment = null, workplaceComments = (day.workplaceComments + (wp to text)).filterValues { it.isNotBlank() })
                } else {
                    day.copy(comment = text.ifBlank { null }, workplaceComments = day.workplaceComments - wp)
                }
                val scope = if (event.onlyHere) state.commentOnlyHere + event.day else state.commentOnlyHere - event.day
                _uiState.update { it.copy(commentOnlyHere = scope) }
                changeDay(event.day) { _, _ -> moved }
            }

            AvailabilityFormEvent.EnableEditMode -> {
                _uiState.update { it.copy(isReadOnly = false) }
            }

            AvailabilityFormEvent.Submit -> submit()
            AvailabilityFormEvent.ClearMessage -> _uiState.update {
                it.copy(
                    error = null,
                    success = false,
                    info = null
                )
            }

            AvailabilityFormEvent.OpenApplyDialog -> _uiState.update { it.copy(showApplyDialog = true) }
            AvailabilityFormEvent.CloseApplyDialog -> _uiState.update { it.copy(showApplyDialog = false) }
            is AvailabilityFormEvent.ApplyTemplateToWeeks -> applyTemplateToWeeks(
                event.templateId,
                event.weekNumbers
            )
            AvailabilityFormEvent.CopyPreviousMonth -> copyPreviousMonth()
            AvailabilityFormEvent.SetAllAvailable -> {
                bulkChange(AvailabilityBulk.setStatus(_uiState.value.sharedDays, _uiState.value.requestedDays(), AvailabilityStatus.AVAILABLE))
                _uiState.update { it.copy(info = UiText.Resource(Res.string.availability_all_set_available)) }
            }
            is AvailabilityFormEvent.ToggleMultiSelect -> _uiState.update {
                it.copy(multiSelected = if (event.day in it.multiSelected) it.multiSelected - event.day else it.multiSelected + event.day)
            }
            is AvailabilityFormEvent.SetStatusForSelected -> {
                val picked = _uiState.value.multiSelected
                if (picked.isNotEmpty()) bulkChange(AvailabilityBulk.setStatus(_uiState.value.sharedDays, picked, event.status))
                _uiState.update { it.copy(multiSelected = emptySet()) }
            }
            AvailabilityFormEvent.ClearMultiSelect -> _uiState.update { it.copy(multiSelected = emptySet()) }
        }
    }

    /** SH-165: shows [shared] as the month (this workplace's view too) and saves it once. */
    private fun bulkChange(shared: Map<Int, AvailabilityDay>) {
        _uiState.update { it.copy(sharedDays = shared, days = display(shared, it.commentOnlyHere, it.availableSlots, it.workplaceId)) }
        autoSave()
    }

    private fun copyPreviousMonth() {
        viewModelScope.launch {
            val state = _uiState.value
            val user = getCurrentUserUseCase().first() ?: return@launch
            val previous = getMyAvailabilityUseCase(user.uid, state.workplaceId, AvailabilityBulk.previousMonth(state.monthIso))
                .getOrNull()?.shared?.days.orEmpty()
            if (previous.isEmpty()) {
                _uiState.update { it.copy(info = UiText.Resource(Res.string.availability_nothing_to_copy)) }
                return@launch
            }
            bulkChange(AvailabilityBulk.copyByWeekday(previous, _uiState.value.sharedDays, state.monthIso, state.requestedDays()))
            _uiState.update { it.copy(info = UiText.Resource(Res.string.availability_copied_previous)) }
        }
    }

    private fun applyTemplateToWeeks(templateId: String, weekNumbers: Set<Int>) {
        viewModelScope.launch {
            val state = _uiState.value
            val template = state.availableTemplates.find { it.id == templateId } ?: return@launch

            val updatedDays = state.sharedDays.toMutableMap()

            state.monthWeeks.filter { it.weekNumber in weekNumbers }.forEach { week ->
                var curr = week.startDate
                while (curr <= week.endDate) {
                    val dayOfWeek = curr.dayOfWeek.isoDayNumber
                    template.days[dayOfWeek]?.let { pref ->
                        updatedDays[curr.day] = AvailabilitySlotMapping.fromWorkplaceDay(pref, state.availableSlots, state.workplaceId)
                    }
                    curr = curr.plus(DatePeriod(days = 1))
                }
            }

            val onlyHere = state.commentOnlyHere + updatedDays.filterValues { it.comment == null && it.workplaceComments.containsKey(state.workplaceId) }.keys
            _uiState.update {
                it.copy(
                    sharedDays = updatedDays,
                    commentOnlyHere = onlyHere,
                    days = display(updatedDays, onlyHere, it.availableSlots, it.workplaceId),
                    showApplyDialog = false
                )
            }
            autoSave()
        }
    }

    private fun autoSave() {
        viewModelScope.launch {
            val state = _uiState.value
            val user = getCurrentUserUseCase().first() ?: return@launch

            // SH-148: in the app's saves, in order (queued), so leaving the screen right after a
            // change keeps it; a failure is said (here, or by the shell once the screen is left).
            saves.run(saveKey, queue = true) {
                submitAvailabilityUseCase.submit(
                    userId = user.uid,
                    workplaceId = state.workplaceId,
                    month = state.monthIso,
                    days = state.sharedDays,
                    isFilled = state.isFilled
                )
            }.onFailure { e -> _uiState.update { it.copy(error = e.toUiText()) } }
        }
    }

    /** SH-148: this month's availability in [saves]. */
    private val saveKey get() = "availability:$rosterId"

    private fun submit() {
        // Set before launching, so a second tap while the first send runs does nothing.
        if (_uiState.value.isSaving) return
        _uiState.update { it.copy(isSaving = true) }
        viewModelScope.launch {
            val state = _uiState.value
            val user = getCurrentUserUseCase().first()
            if (user == null) {
                _uiState.update { it.copy(isSaving = false) }
                return@launch
            }

            // SH-186: offline, the answers sit in this device's cache: after a few seconds the
            // form closes as sent and the shell says so.
            val outcome = saves.runOrKeepOnDevice(saveKey, connectivity.isOnline, queue = true) {
                submitAvailabilityUseCase.submit(
                    userId = user.uid,
                    workplaceId = state.workplaceId,
                    month = state.monthIso,
                    days = state.sharedDays,
                    isFilled = true
                )
            }

            if (outcome.isSavedOrKept) {
                AppTelemetry.log(AnalyticsEvent.AvailabilitySubmitted)
                _uiState.update {
                    it.copy(
                        isSaving = false,
                        success = true,
                        isFilled = true,
                        isReadOnly = true,
                        givenElsewhere = false
                    )
                }
            } else {
                _uiState.update {
                    it.copy(
                        isSaving = false,
                        error = (outcome as? SaveOutcome.Failed)?.error?.toUiText()
                    )
                }
            }
        }
    }

    /** Applies [change] to [day] of the month (a day not given yet is available all day) and saves. */
    private fun changeDay(day: Int, change: (AvailabilityFormUiState, AvailabilityDay) -> AvailabilityDay) {
        _uiState.update { state ->
            val old = state.sharedDays[day] ?: AvailabilityDay(AvailabilityStatus.AVAILABLE)
            val shared = state.sharedDays + (day to change(state, old))
            state.copy(sharedDays = shared, days = display(shared, state.commentOnlyHere, state.availableSlots, state.workplaceId))
        }
        autoSave()
    }

    /**
     * The day form changed [old] (as this workplace shows it) to [pref]: the status, the preferred
     * slot as hours (kept as they were while the slot shown is the same), the comment in its scope.
     */
    private fun edited(state: AvailabilityFormUiState, day: Int, old: AvailabilityDay, pref: DayPreference): AvailabilityDay {
        val shown = state.days[day]
        val slot = state.availableSlots.firstOrNull { it.id == pref.preferredSlotId }
        val preferred = when {
            pref.status == AvailabilityStatus.UNAVAILABLE -> old.preferred
            pref.preferredSlotId == shown?.preferredSlotId -> old.preferred
            slot != null -> listOf(HoursRange(slot.startTime, slot.endTime))
            else -> emptyList()
        }
        val text = pref.comment?.takeIf { it.isNotBlank() }
        val wp = state.workplaceId
        return if (day in state.commentOnlyHere) {
            old.copy(status = pref.status, preferred = preferred,
                workplaceComments = if (text == null) old.workplaceComments - wp else old.workplaceComments + (wp to text))
        } else {
            old.copy(status = pref.status, preferred = preferred, comment = text)
        }
    }

    private fun display(
        shared: Map<Int, AvailabilityDay>,
        onlyHere: Set<Int>,
        slots: List<com.evoapps.evoshift.domain.model.AvailabilitySlot>,
        workplaceId: String
    ): Map<Int, DayPreference> = shared.mapValues { (n, day) ->
        val mapped = AvailabilitySlotMapping.toWorkplaceDay(day, slots, workplaceId)
        // The person's own answer (their hours may miss this workplace's slots, which its
        // managers then read as unavailable), and the comment they edit here.
        DayPreference(
            status = day.status,
            preferredSlotId = mapped.preferredSlotId.takeIf { day.status == AvailabilityStatus.AVAILABLE },
            comment = if (n in onlyHere) day.workplaceComments[workplaceId] else day.comment,
            hours = day.ranges
        )
    }
}
